import React, { useMemo } from 'react'

import { RedDataCell, RedHeadCell, RedRow, RedTable, LabelValueRow } from './_shared/BradescoTable'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import {
  getFieldValueByIdOrFallback,
  getFieldRawValueById,
  getPlaceLevelNameByIndexOrFallback,
  getCidadeUfDoEndereco,
  FALLBACK
} from '../../_helpers/pdfHelpers'
import { HEADING_COLOR } from '../_helpers/brand'
import {
  LOCAL_FIELD_ID_ENDERECO,
  LOCAL_FIELD_ID_CNPJ,
  LOCAL_FIELD_ID_QTD_FUNCIONARIOS,
  LOCAL_PLACE_LEVEL_INDEX_DEPENDENCIA,
  CNAE_FIXO,
  RAMO_ATIVIDADE_FIXO,
  GRAU_DE_RISCO_FIXO,
  EMPRESA_FIXO
} from '../_helpers/mappingIds'

/**
 * Seção 1 - Informações Iniciais.
 * CNAE / Ramo de Atividade / Grau de Risco são texto FIXO do documento (não são campos
 * dinâmicos — confirmado contra o template de referência bioqualynet/pgr, que traz os
 * mesmos três valores hardcoded para o mesmo ramo "Bancos Múltiplos com Carteira
 * Comercial"; não constavam destacados como Campo Adicional na spec fornecida).
 *
 * Estrutura/títulos das subseções corrigidos para bater com o modelo real (confirmado
 * por inspeção pixel-a-pixel): "1.1. IDENTIFICAÇÃO DA UNIDADE" (antes sem título) e
 * "1.2. IDENTIFICAÇÃO DAS DEPENDÊNCIAS" (antes rotulada "1.1. DEPENDÊNCIAS"). A tabela de
 * 1.2 também virou uma tabela em grade de fato (colunas EMPRESA | JUNÇÃO | DEPENDÊNCIAS |
 * QUANTIDADE DE FUNCIONÁRIOS, cabeçalho vermelho/texto branco) em vez do estilo
 * "rótulo: valor" empilhado usado antes — mesmos 4 valores, só reorganizados em colunas.
 *
 * Coluna EMPRESA = `EMPRESA_FIXO` ("Bradesco", ver `_helpers/mappingIds.js`), SEMPRE — não
 * `serviceOrder.place?.name`. Bug reportado por Julia Buzzo (PM, 2026-09-01): a coluna
 * estava mostrando o código da agência (ex. "2160", o mesmo `place.name` usado na capa —
 * ver `getDestinoDaAtividade`) porque a implementação anterior confundiu o Local da
 * atividade com a Empresa do cliente. Não há Campo Adicional/nível de Local de "Empresa"
 * neste documento (o cliente É o Bradesco em todos os casos) — mesmo padrão de texto fixo
 * de CNAE_FIXO/RAMO_ATIVIDADE_FIXO/GRAU_DE_RISCO_FIXO acima.
 *
 * Título "1. INFORMAÇÕES INCIAIS": grafia exatamente como no modelo real (falta o "I" de
 * "INICIAIS" — confirmado como erro de digitação existente no PDF-modelo, não nosso).
 * `PGR_2026_ESPECIFICACAO_TECNICA.md` (regra mestra, seção I.1) é explícito: todo texto
 * não realçado em amarelo no documento de referência é STATIC e deve ser reproduzido
 * caractere a caractere, "inclusive os erros de digitação existentes (INFORMAÇÕES
 * INCIAIS...)" — "Não corrigir, não modernizar, não reescrever." Mantido junto no Sumário
 * (`00b.SumarioSection.js`).
 *
 * Cidade/UF (2026-09-02): ver `getCidadeUfDoEndereco` em `_helpers/pdfHelpers.js` para a
 * causa raiz completa — em resumo, dependiam de um parse por regex em cima do TEXTO já
 * concatenado do Endereço, que nunca isolava Cidade/UF (a regex assumia UF como sigla de
 * 2 letras; o Google Places devolve o nome completo do estado). Corrigido lendo `.city`/
 * `.state` direto do valor CRU (estruturado) do Campo Adicional.
 *
 * CNPJ (2026-09-02, investigado, sem bug de código encontrado): `LOCAL_FIELD_ID_CNPJ`
 * (ID 2) já confirmado correto contra a anotação do PDF-modelo (ver `mappingIds.js`), e o
 * valor desse Campo Adicional é sempre um texto simples (tipo `text`/`cpf_cnpj`, nunca um
 * objeto estruturado como o de Endereço — confirmado em `components/form/DynamicInput.js`
 * e `containers/dynamic/DynamicPresentation.js`: nenhum tratamento especial de objeto
 * para esses tipos, valor sempre string) — `getFieldValueById`/`getFieldValueByIdOrFallback`
 * já tratam string simples corretamente (mesma função, mesmo fix de #740 aplicado a
 * Endereço/CNPJ/Qtd. Funcionários igualmente). Não encontrei, no código, nenhum caminho
 * que explique um "N/A" indevido para CNPJ especificamente — a hipótese mais provável é
 * que o Campo Adicional CNPJ genuinamente não está preenchido neste Local. Recomendo
 * confirmar direto no cadastro do Local (módulo Locais) se o campo está preenchido antes
 * de assumir mais um bug aqui; sem acesso a esse dado real neste ambiente, não dá para
 * confirmar 100%.
 *
 * Junção (2026-09-02, CAUSA RAIZ DEFINITIVA — histórico completo das 3 tentativas
 * anteriores preservado no Git deste arquivo para referência): Junção **não é nível de
 * hierarquia nenhum** — é `serviceOrder.place.name` diretamente, o mesmo campo já usado
 * (corretamente) na capa (`getDestinoDaAtividade`, `pdfHelpers.js`) e que a coluna EMPRESA
 * usava por engano antes de virar `EMPRESA_FIXO`.
 *
 * Como se chegou nisso: Julia Buzzo (PM) enviou um segundo print, desta vez de
 * "Configurações de níveis de locais", mostrando a ordem real dos níveis configurados por
 * ID crescente (mesma ordenação de `places/editV2/sections/placeLevels/_load/
 * LevelsLoader.js`: `sort: { field: ID, type: ASC }`): "Nome da agência" no topo (menor
 * ID), dois níveis em branco/desabilitados no meio, "Junção da agência" na base (maior
 * ID). Cruzando isso com `places/editV2/sections/placeLevels/PlaceLevelInputs.js` — que
 * SEMPRE renderiza o nível de MAIOR ID como o campo de texto livre `name` da folha
 * (`isName = index === levels.length - 1`, `Input={!isName && PlaceLevelSelect}` — só esse
 * nível fica sem seletor, como texto puro) — segue que "Junção da agência" (maior ID) é
 * exatamente esse campo `name`, e não um nível ancestral em `placeLevelsResume`. E como a
 * conta tem 2 níveis desabilitados no meio, a query de `placeLevelsResume` (que não inclui
 * níveis desabilitados) só devolve os ancestrais realmente habilitados — "Nome da agência"
 * (menor ID, um SELECT de nível) não é a folha; a folha é "Junção da agência".
 *
 * Confirmado de forma independente por 3 evidências que se cruzam sem contradição:
 * (1) o texto do PDF-modelo já citava a capa como "Junção/Nome da Dependência - Nome do
 * local da atividade" (ver `getDestinoDaAtividade`) — exatamente `place.name` (Junção) +
 * `place.resume` (nome do local), a mesma ordem já usada na capa;
 * (2) o bug original de EMPRESA usava `place.name` (ex. "2160") por engano nessa coluna —
 * esse valor nunca foi lixo, era o valor de Junção que deveria estar noutra coluna;
 * (3) o filtro lateral "Junção da agência (6)" na listagem de Locais mostra só 6 valores
 * distintos (2160, 279, 464, 662, 982, Bradesco) compartilhados entre muitos Locais —
 * exatamente o padrão esperado de um código de agência/junção reaproveitado por texto
 * livre, não de um nome de Local único por registro.
 *
 * Dependência permanece por posição fixa em `placeLevelsResume`
 * (`LOCAL_PLACE_LEVEL_INDEX_DEPENDENCIA`, Nível 1) — não alterada, por não haver nenhuma
 * evidência nova sobre ela.
 */
const InformacoesIniciaisSection = ({ serviceOrder }) => {
  const fieldValues = serviceOrder.place?.fieldValues || []
  const placeLevelsResume = serviceOrder.place?.placeLevelsResume || []

  const endereco = getFieldValueByIdOrFallback(fieldValues, LOCAL_FIELD_ID_ENDERECO)
  const enderecoValorBruto = getFieldRawValueById(fieldValues, LOCAL_FIELD_ID_ENDERECO)
  const { cidade, uf } = useMemo(
    () => getCidadeUfDoEndereco(enderecoValorBruto, endereco),
    [enderecoValorBruto, endereco]
  )
  const cnpj = getFieldValueByIdOrFallback(fieldValues, LOCAL_FIELD_ID_CNPJ)
  const juncao = serviceOrder.place?.name || FALLBACK
  const dependencias = getPlaceLevelNameByIndexOrFallback(placeLevelsResume, LOCAL_PLACE_LEVEL_INDEX_DEPENDENCIA)
  const qtdFuncionarios = getFieldValueByIdOrFallback(fieldValues, LOCAL_FIELD_ID_QTD_FUNCIONARIOS)

  return (
    <View id="1">
      <Separator weight={15} />
      <Text bold size="small" color={HEADING_COLOR}>
        1. INFORMAÇÕES INCIAIS
      </Text>
      <Separator weight={8} />

      <Text id="1.1" bold size="small" color={HEADING_COLOR}>
        1.1. IDENTIFICAÇÃO DA UNIDADE
      </Text>
      <Separator weight={8} />

      <RedTable width="100%">
        <LabelValueRow label="Endereço" value={endereco} colSpan={2} />
        <LabelValueRow label="Cidade" value={cidade} />
        <LabelValueRow label="UF" value={uf} />
        <LabelValueRow label="CNPJ" value={cnpj} colSpan={2} />
        <LabelValueRow label="CNAE" value={CNAE_FIXO} colSpan={2} />
        <LabelValueRow label="Descrição" value={RAMO_ATIVIDADE_FIXO} colSpan={2} />
        <LabelValueRow label="Grau de Risco" value={GRAU_DE_RISCO_FIXO} colSpan={2} />
      </RedTable>

      <Separator weight={15} />

      <Text id="1.2" bold size="small" color={HEADING_COLOR}>
        1.2. IDENTIFICAÇÃO DAS DEPENDÊNCIAS
      </Text>
      <Separator weight={8} />

      <RedTable width="100%">
        <RedRow>
          <RedHeadCell>EMPRESA</RedHeadCell>
          <RedHeadCell>JUNÇÃO</RedHeadCell>
          <RedHeadCell>DEPENDÊNCIAS</RedHeadCell>
          <RedHeadCell>QUANTIDADE DE FUNCIONÁRIOS</RedHeadCell>
        </RedRow>
        <RedRow>
          <RedDataCell align="center">{EMPRESA_FIXO}</RedDataCell>
          <RedDataCell align="center">{juncao}</RedDataCell>
          <RedDataCell align="center">{dependencias}</RedDataCell>
          <RedDataCell align="center">{qtdFuncionarios}</RedDataCell>
        </RedRow>
      </RedTable>

      <Separator weight={15} />
    </View>
  )
}

export default InformacoesIniciaisSection
